import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Injectable, Input, OnDestroy, Optional, Output, ViewChild } from '@angular/core';
import { ButtonComponent } from 'components/input/button/button.component';
import { EditableContentDirective } from 'components/input/editable-content/editable-content.directive';
import { ScrollableDirective } from 'directives/scrollable.directive';
import { FormValidationService } from 'services/form-validation.service';
import { InputContainerService } from 'services/input-container.service';
import { Subscription } from 'rxjs';

export class EditableContentContext {
	$implicit!: { value: any };
	editMode!: boolean;
	editableContent!: EditableContentDirective;
}
@Injectable()
export class SingleFocusService {
	activeComponent?: EditableContentComponent;
}

@Component({
	selector: 'editable-content, [editable-content]',
	templateUrl: 'editable-content.component.html',
	styleUrls: ['editable-content.component.scss'],
	providers: [InputContainerService, FormValidationService],
	imports: [NgTemplateOutlet, ButtonComponent, NgClass]
})
export class EditableContentComponent implements OnDestroy {

	editableContent!: EditableContentDirective;

	@Input() editEnabled = true;
	@Input() clickToEdit = true;
	@Input() // Forces begin edit via outside event
	set edit(value: boolean) { if (value && !this.editableContent.editing) this.beginEdit(); }
	@Output() valueChanged = new EventEmitter<any>();
	@Output() onEditStart = new EventEmitter<any>();

	@Input() offsetPosition?: ElementRef<HTMLElement>;
	@ViewChild('parent', { read: ElementRef }) private parentElement!: ElementRef<HTMLElement>;

	private _editModeContainer?: ElementRef<HTMLElement>;
	@ViewChild('editModeContainer', { read: ElementRef }) set editModeContainer(value: ElementRef<HTMLElement> | undefined) {
		this._editModeContainer = value;
		this.adjustOffsetTop();
	}
	get editModeContainer(): ElementRef<HTMLElement> | undefined {
		return this._editModeContainer;
	}

	isEditMode = false;
	displayWidth = 0;
	minimumHeight = 0;
	offsetTop?: number;
	stickToBottom = false;
	resizedSubscription?: Subscription;

	confirmEdit() {
		this.valueChanged.emit(this.editableContent.editContext.value);
		this.editableContent.endEdit();
	}
	@HostListener('document:keydown.escape')
	cancelEdit() {
		this.editableContent.endEdit();
	}

	clickedDisplayMode() { if (this.clickToEdit) this.beginEdit(); }

	beginEdit(forceSourceObject?: Object) {
		if (!this.editEnabled) return;
		if (this.singleFocus) {
			if (this.singleFocus.activeComponent && this.singleFocus.activeComponent !== this) {
				if (this.singleFocus.activeComponent!.editableContent.wasChanged()) return;
				this.singleFocus.activeComponent!.editableContent.endEdit();
			}
			this.singleFocus.activeComponent = this;
		}
		this.editableContent.beginEdit(forceSourceObject);
		this.onEditStart.emit(this.editableContent.editContext.value);
	}

	constructor(context: EditableContentContext, private inputContainer: InputContainerService, @Optional() private scrollableParent: ScrollableDirective, @Optional() private singleFocus: SingleFocusService) {

		this.editableContent = context.editableContent;
		this.isEditMode = context.editMode;

		if (!this.isEditMode)
			this.editableContent.getDisplayDimensions = () => {
				const positionOffset = (this.offsetPosition ?? this.parentElement)?.nativeElement;
				const clientRects = positionOffset.getClientRects();
				return {
					width: positionOffset.closest('section')
						? positionOffset.offsetWidth + 2 * 30 // Account for the 30px padding on each side of the edit-mode box when inside a section
						: positionOffset.offsetWidth,
					height: positionOffset.offsetHeight,
					offsetTop: clientRects.length > 0 ? clientRects[0].top : 0
				};
			};
		else {

			const displayDimensions = this.editableContent!.getDisplayDimensions!();
			this.displayWidth = displayDimensions.width;
			this.minimumHeight = displayDimensions.height;
			this.offsetTop = displayDimensions.offsetTop;

			this.scrollableParent?.onScroll.subscribe(() => { this.adjustOffsetTop(); });
			
			setTimeout(() => {
				this.inputContainer.focus();
				this.resizedSubscription = this.inputContainer?.resized.subscribe(() => { this.adjustOffsetTop(); });
				this.adjustOffsetTop();
			});
		}
	}

	ngOnDestroy() {
		this.resizedSubscription?.unsubscribe();
	}

	adjustOffsetTop() {
		this.offsetTop = Math.max(90, this.editableContent!.getDisplayDimensions!().offsetTop);
		if (this.editModeContainer && (this.offsetTop + this.editModeContainer.nativeElement.getClientRects()[0].height) > window.innerHeight) this.stickToBottom = true;
		else this.stickToBottom = false;
	}
}

@Component({
	selector: 'display-content, [display-content]', // TODO: display-content og edit-content virker ikke som attributes på en ng-container
	template: '@if (!editableContent.isEditMode) {<ng-content></ng-content>}'
})
export class EditableContentDisplayComponent {
	constructor(public editableContent: EditableContentComponent) { }
}
@Component({
	selector: 'edit-content, [edit-content]',
	template: '@if (editableContent.isEditMode) {<ng-content></ng-content>}'
})
export class EditableContentEditComponent {
	constructor(public editableContent: EditableContentComponent) {	}
}

export const EditableContent = [EditableContentDirective, EditableContentComponent, EditableContentDisplayComponent, EditableContentEditComponent];
